import { DataKeys } from "../../types";
import { HealthProductStatusBadge } from "./HealthProductStatusBadge";
import { HealthProductSuiteIcon } from "./HealthProductSuiteIcons";
import {
    AimmaProductStatus,
    getLocalized,
    HealthLang,
    isAimmaProductId,
} from "./healthTypes";
import {
    healthProductCapabilityContent,
    healthProductCapabilityDesc,
    healthProductCapabilityDescCompact,
    healthProductCapabilityRow,
    healthProductCapabilityRowCompact,
    healthProductCapabilityTitle,
    healthProductCapabilityTitleCompact,
    healthProductCapabilityTitleRow,
} from "./healthVisualStyles";

interface HealthProductCapabilityRowProps {
    product: DataKeys;
    lang: HealthLang;
    variant?: "default" | "compact";
    muted?: boolean;
    /** Hide title when it duplicates the group heading (e.g. Nursing Robot). */
    hideTitle?: boolean;
}

function resolveStatus(product: DataKeys): AimmaProductStatus | undefined {
    const raw = product?.status as string | undefined;
    if (raw === "under-development") return "under-development";
    if (raw === "available") return "available";
    if (raw === "future-extension") return "future-extension";
    if (raw === "planned-capability") return "planned-capability";
    if (raw === "strategic-extension") return "strategic-extension";
    return undefined;
}

/** Informational product row — no row-level navigation */
export function HealthProductCapabilityRow({
    product,
    lang,
    variant = "default",
    muted = false,
    hideTitle = false,
}: HealthProductCapabilityRowProps) {
    const productId = product?.id as string | undefined;
    const title = getLocalized(product?.title, lang);
    const desc = getLocalized(product?.desc, lang);
    const status = resolveStatus(product);

    const isCompact = variant === "compact";
    const surfaceClass = isCompact
        ? healthProductCapabilityRowCompact
        : healthProductCapabilityRow;
    const titleClass = isCompact
        ? healthProductCapabilityTitleCompact
        : healthProductCapabilityTitle;
    const descClass = isCompact
        ? healthProductCapabilityDescCompact
        : healthProductCapabilityDesc;

    return (
        <div
            className={`${surfaceClass} ${muted ? "cursor-default opacity-90" : "cursor-inherit"}`}
            data-product-id={isAimmaProductId(productId ?? "") ? productId : undefined}
        >
            {productId && (
                <HealthProductSuiteIcon
                    size="product"
                    productId={productId}
                    muted={muted}
                />
            )}
            <div className={healthProductCapabilityContent}>
                <div className={healthProductCapabilityTitleRow}>
                    {hideTitle ? (
                        <span className="sr-only">{title}</span>
                    ) : (
                        <p
                            className={`${titleClass} ${
                                muted ? "text-grey-600" : ""
                            }`}
                        >
                            {title}
                        </p>
                    )}
                    {status && (
                        <HealthProductStatusBadge status={status} lang={lang} />
                    )}
                </div>
                {desc && (
                    <p
                        className={`${descClass} ${
                            muted ? "text-grey-400" : ""
                        }`}
                    >
                        {desc}
                    </p>
                )}
            </div>
        </div>
    );
}
